import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  listTaskAlerts,
  type AlertCategory,
  type AlertTask,
} from '../lib/api';
import { colorHexFor } from '../lib/colors';

/**
 * "What needs attention" widget per ARCH §4.6 — cross-project task alerts.
 *
 *   🔴 past_due           — overdue, top urgency
 *   🟡 due_this_week      — due in next 7 days
 *   🔵 due_next_week      — due in 8-14 days
 *   ⚠️  critical_not_done — flagged critical, not yet complete
 *
 * Two surfaces:
 *   - Home page → no projectId → cross-project view
 *   - Project page → projectId set → filtered to that project
 *
 * Each row links to /projects/:id?expand=:phaseId — the Overview page
 * reads the `expand` query param and auto-expands that phase, scrolling
 * the task into view. (D-100 — PhasePage retired; alerts deep-link
 * back to the inline Overview view which is now the canonical work
 * surface.)
 *
 * D-098 — full visual rebuild. Each alert is now a substantial card with
 * a project color stripe on the left, an eyebrow row carrying project +
 * phase context, and a prominent task line + inline note. Critical tasks
 * show their full note (a deliberate "wow" moment — the most-important
 * tasks surface their full context without a click); non-critical tasks
 * show a 2-line truncated preview.
 */
export function TaskAlertsWidget({ projectId }: { projectId?: string }) {
  const q = useQuery({
    queryKey: ['alerts', 'tasks', projectId ?? 'all'],
    queryFn: () => listTaskAlerts(projectId ? { project_id: projectId } : undefined),
  });

  if (q.isLoading) {
    return (
      <p className="text-sm text-ink-3 italic">Loading alerts…</p>
    );
  }
  if (q.isError) {
    return (
      <p className="text-sm" style={{ color: 'var(--color-danger)' }}>
        Could not load alerts.
      </p>
    );
  }

  const data = q.data;
  if (!data || data.total === 0) {
    return <EmptyState />;
  }

  // Group by category for rendering — preserves the priority order.
  const order: AlertCategory[] = [
    'past_due',
    'due_this_week',
    'due_next_week',
    'critical_not_done',
  ];
  const grouped = new Map<AlertCategory, AlertTask[]>();
  for (const c of order) grouped.set(c, []);
  for (const t of data.tasks) grouped.get(t.category)?.push(t);

  return (
    <div className="space-y-4">
      {/* Header counts strip — pills with colored dots, no emoji glyphs.
          Cleaner / more grown-up than the emoji-led version. */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {order.map((c) => {
          const n = data.counts[c] ?? 0;
          if (n === 0) return null;
          return (
            <span
              key={c}
              className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border"
              style={categoryPillStyle(c)}
              title={categoryLabel(c)}
            >
              <span
                aria-hidden
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: categoryColor(c) }}
              />
              <span className="font-medium">{categoryLabel(c)}</span>
              <span className="num font-semibold">{n}</span>
            </span>
          );
        })}
      </div>

      {/* Rows grouped by category */}
      <ul className="space-y-2.5">
        {order.flatMap((c) => {
          const list = grouped.get(c) ?? [];
          return list.map((t) => (
            <AlertRow key={t.id} task={t} showProject={!projectId} />
          ));
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AlertRow — a deliberate "card" rather than a thin strip. Visual rhythm:
//
//   ┌─[project-color stripe]─────────────────────────────────────────────┐
//   │  PROJECT · PHASE 03 PERMITTING                  CRITICAL · due 4/15 │
//   │                                                                     │
//   │  Permit Set Submitted to AHJ                                        │
//   │  ┃ Submitted via portal 4/12, ref #PM-2026-0418. Waiting on plan-   │
//   │  ┃ review comments — typical 5-7 business day turnaround per pre-   │
//   │  ┃ app meeting notes from 3/8.                                      │
//   └─────────────────────────────────────────────────────────────────────┘
//
// The note block carries a vertical accent bar in the category's color
// (red for past_due / critical, amber for due_this_week, etc.) — gives
// each card a clear urgency cue beyond the colored count pill above.
// ---------------------------------------------------------------------------

function AlertRow({
  task,
  showProject,
}: {
  task: AlertTask;
  showProject: boolean;
}) {
  const c = task.category;
  const projectColor = colorHexFor(task.color_tag);
  const dueLabel = task.due_date
    ? `due ${formatShortDate(task.due_date)}`
    : task.is_critical
      ? 'no due date set'
      : '';

  const isCritical = task.is_critical === 1;

  // Card background: white for elevation against the page-bg / surface-2
  // gray. The category urgency comes through via the small colored pills
  // and the note's accent bar — not a tinted card bg, which would compete
  // with the green Completed pill / project color stripe / etc.
  return (
    <li>
      <Link
        to={`/projects/${task.project_id}?expand=${task.phase_id}`}
        className="group block rounded-lg border overflow-hidden hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent transition-shadow"
        style={{
          background: 'var(--color-card)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="flex items-stretch">
          {/* Project color stripe — full-height, 5px. Same vocabulary the
              ProjectCard / Sidebar uses, so colors carry the same meaning
              across surfaces. */}
          <div
            style={{ background: projectColor, width: 5 }}
            aria-hidden
            className="flex-shrink-0"
            title={`Project: ${task.project_name}`}
          />

          <div className="flex-1 min-w-0 px-4 py-3">
            {/* Eyebrow row — small uppercase project + phase context on the
                left, urgency markers (CRITICAL badge + due date) on the
                right. Compact, gives instant scanning context. */}
            <div className="flex items-center flex-wrap gap-2 mb-1.5">
              {showProject && (
                <>
                  <span className="num text-[11px] font-semibold uppercase tracking-wide text-ink">
                    {task.project_name}
                  </span>
                  <span aria-hidden className="text-ink-3 text-[11px]">·</span>
                </>
              )}
              <span className="num text-[11px] uppercase tracking-wide text-ink-2">
                Phase {String(task.phase_number).padStart(2, '0')} ·{' '}
                <span className="text-ink-3 normal-case tracking-normal">
                  {task.phase_name}
                </span>
              </span>
              <span className="flex-1" />
              {isCritical && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wider"
                  style={{
                    background: 'var(--color-danger)',
                    color: '#FFFFFF',
                  }}
                >
                  Critical
                </span>
              )}
              {dueLabel && (
                <span
                  className="num text-[11px] font-semibold"
                  style={{ color: categoryColor(c) }}
                >
                  {dueLabel}
                </span>
              )}
            </div>

            {/* Task text — the headline of the card. Larger + bolder than
                the eyebrow so the eye lands here first. */}
            <p className="text-base font-semibold text-ink leading-snug">
              {task.task_text}
            </p>

            {/* D-111 — task_note display removed (column dropped).
                Notes are now Client Updates linked to the task. When a
                critical task has linked updates, the user sees them by
                clicking through to the phase (the deep-link below the
                card opens the phase with this task's row visible). */}
            {isCritical && (
              <p className="mt-1 text-xs italic text-ink-3">
                Click to expand the phase. Use the "+ note" affordance on
                the task row to attach context as a Client Update.
              </p>
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}

function EmptyState() {
  return (
    <div
      className="flex items-center gap-3 rounded-lg border px-4 py-5"
      style={{
        background: 'var(--color-card)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div
        className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0"
        style={{
          background: 'var(--color-success-soft)',
          color: 'var(--color-success)',
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
             strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <div>
        <p className="text-sm font-semibold text-ink">All clear</p>
        <p className="text-xs text-ink-2 mt-0.5">
          No tasks need attention right now. Past-due, due-soon, and critical
          tasks will surface here when they exist.
        </p>
      </div>
    </div>
  );
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const m = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, '0');
  const yr = String(d.getFullYear()).slice(-2);
  return `${m}/${day}/${yr}`;
}

function categoryLabel(c: AlertCategory): string {
  switch (c) {
    case 'past_due': return 'Past due';
    case 'due_this_week': return 'Due this week';
    case 'due_next_week': return 'Due next week';
    case 'critical_not_done': return 'Critical';
  }
}

function categoryColor(c: AlertCategory): string {
  switch (c) {
    case 'past_due': return 'var(--color-danger)';
    case 'due_this_week': return 'var(--color-warning)';
    case 'due_next_week': return 'var(--color-info)';
    case 'critical_not_done': return 'var(--color-danger)';
  }
}

function categoryPillStyle(c: AlertCategory): React.CSSProperties {
  const color = categoryColor(c);
  switch (c) {
    case 'past_due':
    case 'critical_not_done':
      return {
        background: 'var(--color-danger-soft)',
        borderColor: 'var(--color-danger)',
        color,
      };
    case 'due_this_week':
      return {
        background: 'var(--color-warning-soft)',
        borderColor: 'var(--color-warning)',
        color,
      };
    case 'due_next_week':
      return {
        background: 'var(--color-card)',
        borderColor: 'var(--color-info)',
        color,
      };
  }
}
